import { useState, useEffect } from 'react';
import {
  Users,
  Target,
  TrendingUp,
  Zap,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import api from '../api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalLeads: 0,
    highOpportunity: 0,
    contacted: 0,
    conversions: 0
  });
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response = await api.get('/leads/');
        const leads = response.data;
        setStats({
          totalLeads: leads.length,
          highOpportunity: leads.filter((l) => l.digital_health_score > 50).length,
          contacted: leads.filter((l) => l.status !== 'New').length,
          conversions: leads.filter((l) => l.status === 'Closed').length
        });

        const last7Days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          return d.toLocaleDateString('en-US', { weekday: 'short' });
        });

        const cData = last7Days.map((day, idx) => ({
          name: day,
          leads: Math.max(5, Math.floor(leads.length / 7) + (idx * 2)),
          conversions: Math.max(1, Math.floor(leads.filter((l) => l.status === 'Closed').length / 7))
        }));
        setChartData(cData);
      } catch (err) {
        console.error('Failed to fetch stats', err);
      }
    };
    fetchLeads();
  }, []);

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400">Overview of your business intelligence</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Leads" value={stats.totalLeads} icon={Users} color="blue" trend="+12%" />
        <StatCard title="High Opportunity" value={stats.highOpportunity} icon={Zap} color="teal" trend="+5%" />
        <StatCard title="Contacted" value={stats.contacted} icon={Target} color="orange" trend="+18%" />
        <StatCard title="Closed Deals" value={stats.conversions} icon={TrendingUp} color="green" trend="+2%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="glass-card p-6 rounded-2xl border border-white/[0.06]">
          <h3 className="text-xl font-semibold mb-6 text-white">Lead Acquisition Trend</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '12px' }}
                  itemStyle={{ color: '#ededed' }}
                />
                <Area type="monotone" dataKey="leads" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorLeads)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card p-6 rounded-2xl border border-white/[0.06]">
          <h3 className="text-xl font-semibold mb-6 text-white">Pipeline Health</h3>
          <div className="space-y-6">
            <HealthItem label="Information Accuracy" value={85} icon={CheckCircle2} color="text-emerald-400" />
            <HealthItem label="Response Rate" value={62} icon={TrendingUp} color="text-indigo-400" />
            <HealthItem label="Data Gaps Found" value={44} icon={AlertCircle} color="text-orange-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, trend }) {
  const colors = {
    blue: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    teal: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  };

  return (
    <div className="glass-card p-6 rounded-2xl border border-white/[0.06] group hover:border-indigo-500/30 transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors[color]} border`}>
          <Icon size={24} />
        </div>
        <span className="text-emerald-400 text-sm font-medium bg-emerald-400/10 px-2 py-1 rounded-lg">
          {trend}
        </span>
      </div>
      <h4 className="text-slate-400 text-sm font-medium">{title}</h4>
      <p className="text-3xl font-bold mt-1 text-white">{value}</p>
    </div>
  );
}

function HealthItem({ label, value, icon: Icon, color }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className={color} />
          <span className="text-sm font-medium text-white">{label}</span>
        </div>
        <span className="text-sm text-slate-400">{value}%</span>
      </div>
      <div className="h-2 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full bg-indigo-500 rounded-full transition-all duration-1000"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}
